import { useState } from "react";
import { PlusIcon, TagIcon } from "@primer/octicons-react";
import { Text } from "@primer/react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ListNoResults, ListSearch } from "../components/ListSearch/ListSearch.jsx";
import { EmptyState } from "../components/EmptyState/EmptyState.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PropertyIdentity, propertyHref } from "../components/PropertyIdentity/PropertyIdentity.jsx";
import { RecordList } from "../components/RecordList/RecordList.jsx";
import { SampleRecordAction } from "../components/SampleRecordAction/SampleRecordAction.jsx";
import { SortableList } from "../components/SortableList/SortableList.jsx";
import { moveProperty } from "../domain/document.js";
import { createSampleProperty } from "../domain/sampleRecords.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./PropertiesPage.css";

export function PropertiesPage({ repository }) {
  const application = useApplicationDocument(repository);
  const [search, setSearch] = useState("");

  async function addSampleProperty() {
    const nextDocument = createSampleProperty(application.document);
    if (await application.save(nextDocument)) navigateToHash(propertyHref(nextDocument.properties.at(-1).id));
  }

  async function reorderProperty(fromIndex, toIndex) {
    await application.save(moveProperty(application.document, fromIndex, toIndex));
  }

  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <PropertiesLoading />;

  const properties = application.document.properties;
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleProperties = properties.filter((property) => (
    !normalizedSearch
    || property.name.toLocaleLowerCase().includes(normalizedSearch)
    || property.values.some((value) => value.name.toLocaleLowerCase().includes(normalizedSearch))
  ));

  return <article className="properties-page">
    <PageHeading description="Define the classifications that apply to every asset." actions={<Button as="a" href="#/properties/new" leadingVisual={PlusIcon}>Add property</Button>}>Properties</PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {properties.length === 0 ? <PropertiesEmpty onAddSample={addSampleProperty} saving={application.saving} /> : <>
      <ListSearch ariaLabel="Search properties" clearLabel="Clear property search" placeholder="Search by property or value" value={search} visibleCount={visibleProperties.length} totalCount={properties.length} onChange={(event) => setSearch(event.target.value)} onClear={() => setSearch("")} />
      {visibleProperties.length ? <RecordList className="properties-list">
        <SortableList
          disabled={Boolean(normalizedSearch)}
          getId={(property) => property.id}
          getLabel={(property) => property.name}
          itemClassName={() => "record-row property-item"}
          items={visibleProperties}
          onMove={reorderProperty}
          preserveHandleSpace
          renderItem={(property) => <div className="property-summary"><PropertyIdentity href={propertyHref(property.id)} property={property} /><Text className="property-value-count">{property.values.length} value{property.values.length === 1 ? "" : "s"}</Text></div>}
        />
      </RecordList> : <ListNoResults heading="No matching properties">Try another property or value name.</ListNoResults>}
    </>}
  </article>;
}

function PropertiesEmpty({ onAddSample, saving }) {
  return <EmptyState
    icon={TagIcon}
    heading="No properties yet"
    description="Properties classify every asset. Their first possible value is always the inherited default."
    action={<Button as="a" href="#/properties/new" leadingVisual={PlusIcon}>Add your first property</Button>}
    secondaryAction={<SampleRecordAction disabled={saving} onSelect={onAddSample}>add a sample property</SampleRecordAction>}
  />;
}

function PropertiesLoading() {
  return <article className="properties-page"><PageHeading description="Loading property definitions…">Properties</PageHeading></article>;
}
